// RootSumCubeClosedTask.js
// (OTWARTE) – suma / różnica dwóch pierwiastków sześciennych, np. ∛24 + ∛192,
// po uproszczeniu w postaci a * ∛b.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

/* ===== helpers ===== */

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function coerceToString(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);

  // event z inputa
  if (typeof val === "object" && val.target && "value" in val.target) {
    return String(val.target.value ?? "");
  }

  // SqrtInput / klawiatura latex: { raw: "..." }
  if (typeof val === "object" && "raw" in val) {
    return String(val.raw ?? "");
  }

  // inne popularne klucze
  if (typeof val === "object" && "latex" in val) return String(val.latex ?? "");
  if (typeof val === "object" && "text" in val) return String(val.text ?? "");
  if (typeof val === "object" && "expr" in val) return String(val.expr ?? "");
  if (typeof val === "object" && "value" in val && typeof val.value !== "object") {
    return String(val.value ?? "");
  }

  return "";
}

function normalizeCanonical(str) {
  return String(str ?? "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-")
    .replace(/\\\\sqrt\[3\]/gi, "∛")
    .replace(/\\sqrt\[3\]/gi, "∛")
    .replace(/sqrt\[3\]/gi, "∛")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/∛\{([^}]+)\}/g, "∛$1")
    .replace(/∛\(([^)]+)\)/g, "∛$1")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .trim();
}

function evaluateExpression(expr) {
  const cleaned = normalizeCanonical(expr);
  if (!cleaned) return NaN;

  try {
    // 2∛3 -> 2*Math.cbrt(3), ∛12 -> Math.cbrt(12)
    let parsed = cleaned
      .replace(/(\d*)∛(\d+)/g, (_, a, b) => `${a || "1"}*Math.cbrt(${b})`)
      .replace(/∛(\d+)/g, (_, b) => `Math.cbrt(${b})`);

    // (gdyby ktoś wpisał √)
    parsed = parsed
      .replace(/(\d*)√(\d+)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+)/g, (_, b) => `Math.sqrt(${b})`);

    return Function(`return ${parsed}`)();
  } catch {
    return NaN;
  }
}

const k = 3;
const CUBEFREE = [2, 3, 5, 6, 7, 10, 11, 13];

function primeFactors(number) {
  const factors = [];
  let remaining = number;
  for (let prime = 2; prime * prime <= remaining; prime += 1) {
    while (remaining % prime === 0) {
      factors.push(prime);
      remaining /= prime;
    }
  }
  if (remaining > 1) factors.push(remaining);
  return factors;
}

function primeFactorLadderLatex(number) {
  let remaining = number;
  const rows = primeFactors(number).map((prime) => {
    const row = `${remaining} & ${prime} \\\\`;
    remaining /= prime;
    return row;
  });
  rows.push("1 & \\\\");
  return `\\begin{array}{r|l}
${rows.join("\n")}
\\end{array}`;
}

function rootLatex(radicand) {
  return `\\sqrt[3]{${radicand}}`;
}

function coeffTimesRootLatex(coeff, d) {
  const inner = rootLatex(d);
  if (coeff === 1) return inner;
  return `${coeff}${inner}`;
}

/* ===== generator ===== */

export function generateTask() {
  let d, aCoeff, bCoeff, op, A, B, resultCoeff;
  let tries = 0;

  while (tries < 200) {
    tries += 1;

    d = pick(CUBEFREE);

    const maxCoeff = 6;
    const coeffs = Array.from({ length: maxCoeff - 1 }, (_, i) => i + 2); // 2..6

    aCoeff = pick(coeffs);
    bCoeff = pick(coeffs);
    if (aCoeff === bCoeff) continue;

    op = pick(["+", "-"]);

    if (op === "-") {
      if (aCoeff < bCoeff) [aCoeff, bCoeff] = [bCoeff, aCoeff];
    }

    resultCoeff = op === "+" ? aCoeff + bCoeff : aCoeff - bCoeff;
    if (resultCoeff <= 0) continue;
    if (resultCoeff === 1) continue;

    A = Math.pow(aCoeff, k) * d;
    B = Math.pow(bCoeff, k) * d;
    if (A > 1800 || B > 1800) continue;

    break;
  }

  const signLatex = op === "-" ? " - " : " + ";
  const exprLatex = `${rootLatex(A)}${signLatex}${rootLatex(B)}`;
  const correctLatex = coeffTimesRootLatex(resultCoeff, d);

  return {
    type: "RootSumCubeClosedTask",
    data: { d, aCoeff, bCoeff, A, B, op, resultCoeff, exprLatex, correctLatex },
    tags: ["dzialania", "pierwiastki", "cuberoot"],
  };
}

/* ===== rendering ===== */

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        Oblicz wartość: <InlineMath math={data.exprLatex} />.
      </p>
      <p style={{ opacity: 0.85, marginTop: -6 }}>
        Zapisz wynik w postaci <InlineMath math={"a\\sqrt[3]{b}"} />.
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={coerceToString(userAnswer)}
      onChange={onChange}
      disabled={disabled}
      wide={true}
      allowCubeRoot={true}
      onlyCubeRoot={true}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const expected = evaluateExpression(data.correctLatex);
  const given = evaluateExpression(coerceToString(userAnswer));

  if (!Number.isFinite(expected) || !Number.isFinite(given)) return false;
  return Math.abs(expected - given) < 0.01;
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.correctLatex} />;
}

export function renderUserAnswer(userAnswer) {
  const s = coerceToString(userAnswer);
  if (!s || s.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={normalizeCanonical(s)} />;
}

export function renderExplanation({ data }) {
  const { d, aCoeff, bCoeff, A, B, op, resultCoeff, correctLatex } = data;

  const factorLineA = `${rootLatex(A)}= \\sqrt[3]{${aCoeff}^{3}\\cdot ${d}}= ${coeffTimesRootLatex(
    aCoeff,
    d
  )}`;
  const factorLineB = `${rootLatex(B)}= \\sqrt[3]{${bCoeff}^{3}\\cdot ${d}}= ${coeffTimesRootLatex(
    bCoeff,
    d
  )}`;
  const combined = `${coeffTimesRootLatex(aCoeff, d)} ${op} ${coeffTimesRootLatex(
    bCoeff,
    d
  )} = ${coeffTimesRootLatex(resultCoeff, d)}`;

  return (
    <div className="space-y-2">
      <p>
        Rozkładamy obie liczby pod pierwiastkami w zapisie słupkowym:
      </p>

      <div style={{ display: "flex", gap: 32, flexWrap: "wrap", justifyContent: "center" }}>
        <BlockMath math={primeFactorLadderLatex(A)} />
        <BlockMath math={primeFactorLadderLatex(B)} />
      </div>

      <p>Z trójek jednakowych czynników wyłączamy czynniki przed pierwiastek:</p>
      <BlockMath math={factorLineA} />
      <BlockMath math={factorLineB} />

      <p>
        Teraz łączymy jednomianowe pierwiastki:
      </p>

      <BlockMath math={combined} />

      <p>Odpowiedź:</p>
      <BlockMath math={correctLatex} />
    </div>
  );
}
